import React, { createContext, useContext, useState, useEffect } from 'react';

export const THEMES = {
  classic: {
    id: 'classic',
    name: 'Classic',
    primary: '#002147',
    primaryHover: '#001a39',
    primaryLight: '#EFF3F7',
    secondary: '#708090',
    divider: '#708090',
    bgPage: '#F4F4F4',
    swatch: '#002147',
    swatchLabel: 'Navy',
    font: "system-ui, -apple-system, 'Segoe UI', sans-serif",
  },
  urbanist: {
    id: 'urbanist',
    name: 'Urbanist',
    primary: '#8A70D6',
    primaryHover: '#7560c4',
    primaryLight: '#D6CAF8',
    secondary: '#424040',
    divider: '#D6CAF8',
    bgPage: '#F8F5FF',
    swatch: '#8A70D6',
    swatchLabel: 'Purple',
    font: "'Urbanist', system-ui, sans-serif",
  },
  neumorphism: {
    id: 'neumorphism',
    name: 'Neumorphism',
    primary: '#7c6f64',
    primaryHover: '#6b5e53',
    primaryLight: '#ece9e3',
    secondary: '#8a8078',
    divider: '#d4cfc7',
    bgPage: '#ece9e3',
    swatch: '#ece9e3',
    swatchLabel: 'Ivory',
    font: "system-ui, -apple-system, 'Segoe UI', sans-serif",
  },
};

// CSS overrides for Urbanist theme — targets compiled Tailwind class selectors
// so all pages get themed automatically without touching every component.
const URBANIST_CSS = `
html[data-theme="urbanist"] * {
  font-family: 'Urbanist', system-ui, sans-serif !important;
}
html[data-theme="urbanist"] .bg-\\[#002147\\] {
  background-color: #8A70D6 !important;
}
html[data-theme="urbanist"] .hover\\:bg-\\[#001a39\\]:hover {
  background-color: #7560c4 !important;
}
html[data-theme="urbanist"] .text-\\[#002147\\] {
  color: #8A70D6 !important;
}
html[data-theme="urbanist"] .border-\\[#002147\\] {
  border-color: #8A70D6 !important;
}
html[data-theme="urbanist"] .hover\\:text-\\[#002147\\]:hover {
  color: #8A70D6 !important;
}
html[data-theme="urbanist"] .hover\\:bg-\\[#F4F4F4\\]:hover {
  background-color: #D6CAF8 !important;
}
html[data-theme="urbanist"] .bg-\\[#F4F4F4\\] {
  background-color: #F8F5FF !important;
}
html[data-theme="urbanist"] .text-\\[#708090\\] {
  color: #424040 !important;
}
html[data-theme="urbanist"] .border-\\[#708090\\] {
  border-color: #D6CAF8 !important;
}
html[data-theme="urbanist"] .hover\\:text-\\[#708090\\]:hover {
  color: #424040 !important;
}
`;

// CSS overrides for Neumorphism theme — warm ivory palette with soft 3D shadows
const NEUMORPHISM_CSS = `
/* ── Color swaps ─────────────────────────────────── */
html[data-theme="neumorphism"] .bg-\\[#002147\\] {
  background-color: #7c6f64 !important;
}
html[data-theme="neumorphism"] .hover\\:bg-\\[#001a39\\]:hover {
  background-color: #6b5e53 !important;
}
html[data-theme="neumorphism"] .text-\\[#002147\\] {
  color: #7c6f64 !important;
}
html[data-theme="neumorphism"] .border-\\[#002147\\] {
  border-color: #7c6f64 !important;
}
html[data-theme="neumorphism"] .hover\\:text-\\[#002147\\]:hover {
  color: #7c6f64 !important;
}
html[data-theme="neumorphism"] .bg-\\[#F4F4F4\\] {
  background-color: #ece9e3 !important;
}
html[data-theme="neumorphism"] .hover\\:bg-\\[#F4F4F4\\]:hover {
  background-color: #e2dfd8 !important;
}
html[data-theme="neumorphism"] .text-\\[#708090\\] {
  color: #8a8078 !important;
}
html[data-theme="neumorphism"] .border-\\[#708090\\] {
  border-color: #d4cfc7 !important;
}
html[data-theme="neumorphism"] .hover\\:text-\\[#708090\\]:hover {
  color: #8a8078 !important;
}
html[data-theme="neumorphism"] .hover\\:bg-\\[#002147\\]:hover {
  background-color: #7c6f64 !important;
}
html[data-theme="neumorphism"] .border-l-4.border-\\[#002147\\] {
  border-left-color: #7c6f64 !important;
}

/* ── Page background ─────────────────────────────── */
html[data-theme="neumorphism"] body {
  background-color: #ece9e3 !important;
}

/* ── Neumorphic cards & panels ───────────────────── */
html[data-theme="neumorphism"] .bg-white {
  background-color: #ece9e3 !important;
  box-shadow: 6px 6px 12px #c5beb0, -6px -6px 12px #ffffff !important;
  border-color: transparent !important;
}

/* ── Neumorphic shadows replace flat shadows ─────── */
html[data-theme="neumorphism"] .shadow-sm {
  box-shadow: 3px 3px 6px #c5beb0, -3px -3px 6px #ffffff !important;
}
html[data-theme="neumorphism"] .shadow-md,
html[data-theme="neumorphism"] .shadow {
  box-shadow: 5px 5px 10px #c5beb0, -5px -5px 10px #ffffff !important;
}
html[data-theme="neumorphism"] .shadow-lg {
  box-shadow: 8px 8px 16px #c5beb0, -8px -8px 16px #ffffff !important;
}
html[data-theme="neumorphism"] .shadow-xl {
  box-shadow: 10px 10px 20px #c5beb0, -10px -10px 20px #ffffff !important;
}
html[data-theme="neumorphism"] .hover\\:shadow-lg:hover {
  box-shadow: 8px 8px 16px #c5beb0, -8px -8px 16px #ffffff !important;
}
html[data-theme="neumorphism"] .hover\\:shadow-md:hover {
  box-shadow: 5px 5px 10px #c5beb0, -5px -5px 10px #ffffff !important;
}

/* ── Inputs, selects, textareas — inset neumorphic ─ */
html[data-theme="neumorphism"] input:not([type="checkbox"]):not([type="radio"]):not([type="color"]),
html[data-theme="neumorphism"] select,
html[data-theme="neumorphism"] textarea {
  background-color: #ece9e3 !important;
  box-shadow: inset 3px 3px 6px #c5beb0, inset -3px -3px 6px #ffffff !important;
  border-color: transparent !important;
}
html[data-theme="neumorphism"] input:focus,
html[data-theme="neumorphism"] select:focus,
html[data-theme="neumorphism"] textarea:focus {
  border-color: #7c6f64 !important;
  outline-color: #7c6f64 !important;
}

/* ── Table styling ───────────────────────────────── */
html[data-theme="neumorphism"] table {
  background-color: #ece9e3 !important;
  border-color: #d4cfc7 !important;
}
html[data-theme="neumorphism"] thead {
  background-color: #e2dfd8 !important;
}
html[data-theme="neumorphism"] th {
  background-color: #e2dfd8 !important;
  color: #5c524a !important;
}
html[data-theme="neumorphism"] tr:hover td {
  background-color: #e6e3dc !important;
}
html[data-theme="neumorphism"] td {
  border-color: #d4cfc7 !important;
}

/* ── Buttons with white bg — raised neumorphic ──── */
html[data-theme="neumorphism"] button.bg-white,
html[data-theme="neumorphism"] a.bg-white {
  background-color: #ece9e3 !important;
  box-shadow: 4px 4px 8px #c5beb0, -4px -4px 8px #ffffff !important;
  border-color: transparent !important;
}
html[data-theme="neumorphism"] button.bg-white:hover,
html[data-theme="neumorphism"] a.bg-white:hover {
  box-shadow: 2px 2px 4px #c5beb0, -2px -2px 4px #ffffff !important;
}
html[data-theme="neumorphism"] button.bg-white:active,
html[data-theme="neumorphism"] a.bg-white:active {
  box-shadow: inset 3px 3px 6px #c5beb0, inset -3px -3px 6px #ffffff !important;
}

/* ── Modals & dropdowns ──────────────────────────── */
html[data-theme="neumorphism"] .fixed .bg-white,
html[data-theme="neumorphism"] [class*="modal"] .bg-white {
  background-color: #ece9e3 !important;
  box-shadow: 10px 10px 20px #c5beb0, -10px -10px 20px #ffffff !important;
}

/* ── Gray utility bg ─────────────────────────────── */
html[data-theme="neumorphism"] .bg-gray-50 {
  background-color: #f0ede7 !important;
}
html[data-theme="neumorphism"] .bg-gray-100 {
  background-color: #ece9e3 !important;
}
html[data-theme="neumorphism"] .bg-gray-200 {
  background-color: #e2dfd8 !important;
}

/* ── Border softening ────────────────────────────── */
html[data-theme="neumorphism"] .border-gray-200 {
  border-color: #d4cfc7 !important;
}
html[data-theme="neumorphism"] .border-gray-300 {
  border-color: #c5beb0 !important;
}
html[data-theme="neumorphism"] .divide-gray-200 > :not(:last-child) {
  border-color: #d4cfc7 !important;
}
`;

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(
    () => { try { return localStorage.getItem('crm-theme') || 'classic'; } catch { return 'classic'; } }
  );

  const theme = THEMES[themeId] || THEMES.classic;

  useEffect(() => {
    try { localStorage.setItem('crm-theme', themeId); } catch {}
    document.documentElement.setAttribute('data-theme', themeId);

    let styleEl = document.getElementById('crm-theme-overrides');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'crm-theme-overrides';
      document.head.appendChild(styleEl);
    }
    const cssMap = { urbanist: URBANIST_CSS, neumorphism: NEUMORPHISM_CSS };
    styleEl.textContent = cssMap[themeId] || '';

    // Apply page font globally
    document.body.style.fontFamily = theme.font;
  }, [themeId, theme.font]);

  return (
    <ThemeContext.Provider value={{ theme, themeId, setThemeId, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
